import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} ProductFrameAddonRefs
 * @property {HTMLInputElement} checkbox - The "add a frame" checkbox
 * @property {HTMLSelectElement | HTMLInputElement} variantSelect - The selected frame variant (select or hidden input)
 */

/**
 * Adds an optional linked product (e.g. a frame) to the cart alongside the
 * main product, as a separate line item, when the product form is submitted.
 * Runs independently of the main add-to-cart flow - it never intercepts or
 * blocks the primary submission, it only adds a second item on success.
 *
 * @extends {Component<ProductFrameAddonRefs>}
 */
class ProductFrameAddon extends Component {
  requiredRefs = ['checkbox', 'variantSelect'];

  /** @type {HTMLFormElement | null} */
  #form = null;

  #handleSubmit = () => {
    if (!this.refs.checkbox.checked) return;

    const variantId = this.refs.variantSelect.value;
    if (!variantId) return;

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((response) => response.json())
      .then((item) => {
        if (item.status) return;

        document.dispatchEvent(
          new CartUpdateEvent({}, this.id || 'product-frame-addon', {
            itemCount: 1,
            source: 'product-form-component',
          })
        );
      })
      .catch(() => {});
  };

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]') ?? null;
    this.#form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }
}

if (!customElements.get('product-frame-addon')) {
  customElements.define('product-frame-addon', ProductFrameAddon);
}
